<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import AttributesAPI from 'dashboard/api/attributes';
import {
  VARIABLE_GROUPS,
  OPERATORS_BY_TYPE,
  OPERATORS_NEEDING_VALUE,
  findVariable,
  customGroupForDefinitions,
} from '../conditionVariables';

const props = defineProps({
  modelValue: { type: Object, required: true }, // { conditions: [{ and_conditions: [{...}] }] }
});
const emit = defineEmits(['update:modelValue']);

const customDefs = ref([]);
const openPickerFor = ref(null); // 'block-{i}-cond-{j}' o null
const pickerSearch = ref('');

onMounted(async () => {
  try {
    const { data } = await AttributesAPI.listByModel('deal_attribute');
    customDefs.value = data || [];
  } catch (e) { /* silent */ }
});

const allGroups = computed(() => {
  const customGroup = customGroupForDefinitions(customDefs.value);
  return customGroup ? [...VARIABLE_GROUPS, customGroup] : VARIABLE_GROUPS;
});

const filteredGroups = computed(() => {
  const q = pickerSearch.value.trim().toLowerCase();
  if (!q) return allGroups.value;
  return allGroups.value
    .map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(q)) }))
    .filter(g => g.items.length > 0);
});

// Inicializar la estructura si está vacía o tiene formato viejo
function ensureStructure() {
  if (!props.modelValue.conditions || !Array.isArray(props.modelValue.conditions)) {
    update({ ...props.modelValue, conditions: [{ and_conditions: [emptyCondition()] }] });
  }
}
ensureStructure();
watch(() => props.modelValue, ensureStructure, { deep: true });

function emptyCondition() {
  return { variable: 'message.text', operator: 'equals', value: '', value_type: 'manual' };
}

function update(newValue) {
  emit('update:modelValue', newValue);
}

function operatorsFor(variableKey) {
  const v = findVariable(variableKey, customDefs.value);
  if (!v) return OPERATORS_BY_TYPE.text;
  return OPERATORS_BY_TYPE[v.type] || OPERATORS_BY_TYPE.text;
}

function variableLabel(key) {
  const v = findVariable(key, customDefs.value);
  return v?.label || key;
}

function operatorLabel(variableKey, opKey) {
  const ops = operatorsFor(variableKey);
  return ops.find(o => o.key === opKey)?.label || opKey;
}

function needsValue(opKey) {
  return OPERATORS_NEEDING_VALUE.includes(opKey);
}

function selectVariable(blockIdx, condIdx, variableKey) {
  const conditions = JSON.parse(JSON.stringify(props.modelValue.conditions || []));
  const cond = conditions[blockIdx].and_conditions[condIdx];
  cond.variable = variableKey;
  // Reset operator si el actual no es válido para el tipo nuevo
  const newOps = operatorsFor(variableKey);
  if (!newOps.find(o => o.key === cond.operator)) {
    cond.operator = newOps[0]?.key || 'equals';
  }
  update({ ...props.modelValue, conditions });
  openPickerFor.value = null;
  pickerSearch.value = '';
}

function updateCond(blockIdx, condIdx, field, value) {
  const conditions = JSON.parse(JSON.stringify(props.modelValue.conditions || []));
  conditions[blockIdx].and_conditions[condIdx][field] = value;
  update({ ...props.modelValue, conditions });
}

function addAndCondition(blockIdx) {
  const conditions = JSON.parse(JSON.stringify(props.modelValue.conditions || []));
  conditions[blockIdx].and_conditions.push(emptyCondition());
  update({ ...props.modelValue, conditions });
}

function removeCondition(blockIdx, condIdx) {
  const conditions = JSON.parse(JSON.stringify(props.modelValue.conditions || []));
  conditions[blockIdx].and_conditions.splice(condIdx, 1);
  if (conditions[blockIdx].and_conditions.length === 0) {
    conditions.splice(blockIdx, 1);
  }
  if (conditions.length === 0) {
    conditions.push({ and_conditions: [emptyCondition()] });
  }
  update({ ...props.modelValue, conditions });
}

function addBlock() {
  const conditions = JSON.parse(JSON.stringify(props.modelValue.conditions || []));
  conditions.push({ and_conditions: [emptyCondition()] });
  update({ ...props.modelValue, conditions });
}

function pickerKey(blockIdx, condIdx) {
  return `${blockIdx}-${condIdx}`;
}

function togglePicker(blockIdx, condIdx) {
  const k = pickerKey(blockIdx, condIdx);
  openPickerFor.value = openPickerFor.value === k ? null : k;
  pickerSearch.value = '';
}
</script>

<template>
  <div class="space-y-2">
    <div
      v-for="(block, blockIdx) in (modelValue.conditions || [])"
      :key="`block-${blockIdx}`"
      class="bg-n-alpha-1 border border-n-weak rounded-lg p-3"
    >
      <div
        v-for="(cond, condIdx) in block.and_conditions"
        :key="`cond-${blockIdx}-${condIdx}`"
      >
        <div class="flex items-start flex-wrap gap-1.5 text-sm">
          <svg class="w-3.5 h-3.5 text-n-slate-11 mt-1.5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
          <span class="text-n-slate-11 mt-1">Si</span>
          <!-- Variable picker trigger -->
          <div class="relative">
            <button
              type="button"
              @click="togglePicker(blockIdx, condIdx)"
              class="underline decoration-dotted underline-offset-2 text-n-slate-12 hover:text-blue-500 px-1 py-0.5"
            >{{ variableLabel(cond.variable) }}</button>
            <!-- Dropdown -->
            <div
              v-if="openPickerFor === pickerKey(blockIdx, condIdx)"
              class="absolute top-full left-0 mt-1 w-72 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-80 overflow-hidden flex flex-col"
            >
              <div class="p-2 border-b border-n-weak">
                <input v-model="pickerSearch" placeholder="Buscar variable…" class="w-full bg-n-alpha-1 border border-n-weak rounded px-2 py-1 text-xs text-n-slate-12" />
              </div>
              <div class="overflow-y-auto p-2">
                <div v-for="g in filteredGroups" :key="g.label" class="mb-2">
                  <div class="text-[9px] uppercase tracking-wider text-n-slate-11 mb-1 px-1">{{ g.label }}</div>
                  <button
                    v-for="i in g.items"
                    :key="i.key"
                    type="button"
                    @click="selectVariable(blockIdx, condIdx, i.key)"
                    class="w-full text-left px-2 py-1 text-xs rounded text-n-slate-12 hover:bg-blue-500/10"
                    :class="cond.variable === i.key ? 'bg-blue-500/15 text-blue-600 dark:text-blue-300 font-medium' : ''"
                  >{{ i.label }}</button>
                </div>
                <div v-if="filteredGroups.length === 0" class="text-xs text-n-slate-11 italic text-center py-2">Sin resultados</div>
              </div>
            </div>
          </div>
          <!-- Operator -->
          <select
            :value="cond.operator"
            @change="updateCond(blockIdx, condIdx, 'operator', $event.target.value)"
            class="bg-transparent border-0 text-blue-500 underline decoration-dotted underline-offset-2 text-sm py-0.5 px-1 cursor-pointer focus:outline-none"
          >
            <option v-for="op in operatorsFor(cond.variable)" :key="op.key" :value="op.key">{{ op.label }}</option>
          </select>
          <!-- Value (si aplica) -->
          <template v-if="needsValue(cond.operator)">
            <input
              :value="cond.value"
              @input="updateCond(blockIdx, condIdx, 'value', $event.target.value)"
              class="bg-n-alpha-1 border border-n-weak rounded px-2 py-0.5 text-xs text-n-slate-12 min-w-[120px]"
              placeholder="Valor"
            />
          </template>
          <button
            type="button"
            @click="removeCondition(blockIdx, condIdx)"
            class="ml-auto text-n-slate-11 hover:text-rose-500 text-base leading-none"
            title="Eliminar condición"
          >×</button>
        </div>
        <div v-if="condIdx < block.and_conditions.length - 1" class="text-[10px] text-n-slate-11 italic text-center my-2 border-t border-n-weak pt-2">─── Y ───</div>
      </div>
      <button
        type="button"
        @click="addAndCondition(blockIdx)"
        class="text-[11px] text-n-slate-11 hover:text-blue-500 mt-2"
      >+ Agregar condición (y)</button>
    </div>

    <button
      type="button"
      @click="addBlock"
      class="text-xs text-blue-500 hover:underline"
    >+ Agregar siguiente bloque (o)</button>

    <div class="border-t border-n-weak pt-2 mt-2 text-[11px] text-n-slate-11 italic flex items-center gap-2">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
      Ninguna de las condiciones <span class="ml-auto">→ rama "false"</span>
    </div>
  </div>
</template>
